/**
 * Virtual Warehouse — page shell (mirrors `SaleControlPage`).
 *
 * Resolves the active tab from the URL segment:
 *   /virtual-warehouse/received          → Received datasheet
 *   /virtual-warehouse/quality-discount  → Quality Discount datasheet
 * Default = received. Renders an in-page tab strip + the active tab's view, and
 * wires a single ReloadButton to the active tab's list endpoint.
 */
import { Box, Button, Card, CardContent, CircularProgress, Stack, Tab, Tabs } from '@mui/material'
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import DownloadIcon from '@mui/icons-material/Download'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import PageHeader from '../../components/PageHeader'
import ReloadButton from '../../components/ReloadButton'
import { CARD_CONTENT_COMPACT, FILTER_HEADER_INPUT_SX, HIDE_ON_TABLET_INLINE } from '../../theme/responsive'
import { usePreference } from '../../hooks/usePreference'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'
import { type VwPageFilter } from '../../store/api/virtualWarehouseApi'
import { API_BASE_URL } from '../../config/api'
import { useAppSelector } from '../../store'
import VirtualWarehouseReceivedView from './VirtualWarehouseReceivedView'
import VirtualWarehouseQualityDiscountView from './VirtualWarehouseQualityDiscountView'

/** Parse a Content-Disposition header into a download filename (copy of the
 *  Sale Contract helper). */
const getDownloadFilename = (disposition: string | null): string | null => {
  if (!disposition) return null

  const filenameStar = /filename\*\s*=\s*([^;]+)/i.exec(disposition)
  if (filenameStar?.[1]) {
    const value = filenameStar[1].trim().replace(/^UTF-8''/i, '').replace(/^"|"$/g, '')
    try {
      return decodeURIComponent(value)
    } catch {
      return value
    }
  }

  const filename = /filename\s*=\s*([^;]+)/i.exec(disposition)
  if (!filename?.[1]) return null
  return filename[1].trim().replace(/^"|"$/g, '')
}

type VwTabKey = 'received' | 'quality-discount'

const TAB_CONFIG: Array<{ key: VwTabKey; label: string }> = [
  { key: 'received', label: 'Received' },
  { key: 'quality-discount', label: 'Quality Discount' },
]

function isVwTabKey(value: string | undefined): value is VwTabKey {
  return value === 'received' || value === 'quality-discount'
}

export default function VirtualWarehousePage() {
  const location = useLocation()
  const navigate = useNavigate()

  const activeKey = useMemo<VwTabKey>(() => {
    const segments = location.pathname.split('/').filter(Boolean)
    const candidate = segments[1]
    return isVwTabKey(candidate) ? candidate : 'received'
  }, [location.pathname])

  useEffect(() => {
    if (!location.pathname.startsWith('/virtual-warehouse')) return
    const segments = location.pathname.split('/').filter(Boolean)
    if (!isVwTabKey(segments[1])) {
      navigate('/virtual-warehouse/received', { replace: true })
    }
  }, [location.pathname, navigate])

  // Both list endpoints provide the single `VirtualWarehouse` tag; watch the
  // active tab's endpoint so the spinner reflects the visible list.
  const watch = useMemo<string[]>(
    () => (activeKey === 'received' ? ['getReceivedList'] : ['getQualityDiscountList']),
    [activeKey],
  )

  // ── Shared common-block state (mirrors Access frmVirtualFactoryMain header) ──
  // "Date in" filter + Export Excel live at the page level (shared block), not in
  // the tab toolbar. Only the Received tab uses date_in, so the block shows there.
  const snack = useSnackbar()
  const pageFilter = usePreference<VwPageFilter>('virtual_warehouse', 'page_filter')
  const { dateInFrom, dateInTo } = pageFilter.value
  const email = useAppSelector((s) => s.auth.user?.email ?? '')
  const exportToken = 'JDHK-NDEB-DBCK-DGEJ'
  const [exporting, setExporting] = useState<'all' | 'filter' | null>(null)

  const downloadExport = async (mode: 'all' | 'filter') => {
    if (!email) return
    setExporting(mode)
    try {
      // Both hit the legacy api_erp_prod endpoints (like Sale Contract). All →
      // /download/virtualwh/data (mirrors Access btnExportExcel); Filter → the
      // sibling /data_filter with the shared "Date in" range.
      const base = `${API_BASE_URL}/erp-legacy/download/virtualwh`
      const url =
        mode === 'filter'
          ? `${base}/data_filter?token=${encodeURIComponent(exportToken)}&email=${encodeURIComponent(email)}&start_date=${encodeURIComponent(dateInFrom ?? '')}&end_date=${encodeURIComponent(dateInTo ?? '')}`
          : `${base}/data?token=${encodeURIComponent(exportToken)}&email=${encodeURIComponent(email)}`
      const res = await fetch(url)
      if (!res.ok) throw new Error('Export failed')
      const blob = await res.blob()
      const filename = getDownloadFilename(res.headers.get('content-disposition'))
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = href
      a.download = filename ?? (mode === 'filter' ? 'VirtualWH_filter.xlsx' : 'VirtualWH.xlsx')
      a.click()
      URL.revokeObjectURL(href)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Export failed'))
    } finally {
      setExporting(null)
    }
  }

  const headerButtonSx = {
    minHeight: 24,
    px: 0.75,
    py: 0.15,
    fontSize: '0.67rem',
    lineHeight: 1.1,
    textTransform: 'none' as const,
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <PageHeader
        title="Virtual Warehouse"
        module="Virtual Warehouse"
        actions={
          <Stack
            direction="row"
            spacing={0.45}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
            justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
            sx={{ width: '100%' }}
          >
            {/* Shared common block for the whole Virtual Warehouse page (mirrors
                Access frmVirtualFactoryMain header): "Date in" filter + Export +
                Reload, visible on both tabs. date_in only affects Received. */}
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Date in"
                format="DD-MMM-YYYY"
                value={dateInFrom ? dayjs(dateInFrom) : null}
                onChange={(d) => void pageFilter.patch({ dateInFrom: d ? d.format('YYYY-MM-DD') : null })}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: FILTER_HEADER_INPUT_SX,
                    InputLabelProps: { shrink: true },
                    inputProps: { 'aria-label': 'Date in from' },
                  },
                }}
              />
              <ArrowRightAltIcon
                fontSize="small"
                sx={{ color: 'text.secondary', fontSize: { xs: 16, sm: 18 }, mx: 0.25 }}
                aria-hidden="true"
              />
              <DatePicker
                format="DD-MMM-YYYY"
                value={dateInTo ? dayjs(dateInTo) : null}
                onChange={(d) => void pageFilter.patch({ dateInTo: d ? d.format('YYYY-MM-DD') : null })}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: FILTER_HEADER_INPUT_SX,
                    inputProps: { 'aria-label': 'Date in to' },
                  },
                }}
              />
            </LocalizationProvider>
            <ReloadButton sx={headerButtonSx} apiTags={['VirtualWarehouse']} baseTags={[]} watchEndpoints={watch} />
            <Button
              variant="outlined"
              size="small"
              sx={headerButtonSx}
              disabled={exporting !== null}
              onClick={() => void downloadExport('filter')}
              title="Export (Filter)"
              startIcon={exporting === 'filter' ? <CircularProgress size={11} color="inherit" /> : <DownloadIcon fontSize="small" />}
            >
              {/* xs+sm: "Filter" only (discriminator) — md+: "Export (Filter)" */}
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Export ({' '}</Box>
              Filter
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>)</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={headerButtonSx}
              disabled={exporting !== null}
              onClick={() => void downloadExport('all')}
              title="Export (All)"
              startIcon={exporting === 'all' ? <CircularProgress size={11} color="inherit" /> : <DownloadIcon fontSize="small" />}
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Export ({' '}</Box>
              All
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>)</Box>
            </Button>
          </Stack>
        }
      />

      <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Tabs
          value={activeKey}
          onChange={(_, value: VwTabKey) => navigate(`/virtual-warehouse/${value}`, { replace: false })}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 34,
            '& .MuiTabs-indicator': { height: 2.5, borderRadius: '3px 3px 0 0' },
            '& .MuiTab-root': {
              position: 'relative',
              minHeight: 34,
              minWidth: 0,
              py: 0.25,
              px: 1.5,
              fontSize: '0.78rem',
              fontWeight: 600,
              letterSpacing: 0.15,
              textTransform: 'none',
              color: 'text.secondary',
              borderRadius: '6px 6px 0 0',
              transition: 'color 140ms ease, background-color 140ms ease',
              '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
              '&.Mui-selected': { color: 'primary.main', fontWeight: 700 },
              '&:not(:last-of-type)::after': {
                content: '""',
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                height: 16,
                width: '1px',
                bgcolor: 'divider',
                opacity: 0.6,
                transition: 'opacity 140ms ease',
              },
              '&:hover::after, &.Mui-selected::after': { opacity: 0 },
            },
          }}
        >
          {TAB_CONFIG.map((tab) => (
            <Tab key={tab.key} label={tab.label} value={tab.key} />
          ))}
        </Tabs>

        <CardContent sx={CARD_CONTENT_COMPACT}>
          <Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
            {activeKey === 'received' ? (
              <VirtualWarehouseReceivedView />
            ) : (
              <VirtualWarehouseQualityDiscountView />
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  )
}
